import { AlertTriangle, Clock, Target, ChevronRight } from 'lucide-react';

interface ProjectStatus {
  projectCode: string;
  projectName: string;
  completion: number;
  onTimeProbability: number;
  risks: number;
  challenges: string[];
}

interface ProgramProjectsStatusProps {
  projects: ProjectStatus[];
  onProjectClick: (projectCode: string) => void;
}

export function ProgramProjectsStatus({ projects, onProjectClick }: ProgramProjectsStatusProps) {
  const getOnTimeProbabilityColor = (probability: number) => {
    if (probability >= 80) return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' };
    if (probability >= 60) return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
    if (probability >= 40) return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
    return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' };
  };

  const getRiskColor = (riskCount: number) => {
    if (riskCount === 0) return 'text-green-600';
    if (riskCount <= 2) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Project Status Overview</h4>
      
      <div className="space-y-2">
        {projects.map((project) => {
          const probabilityColors = getOnTimeProbabilityColor(project.onTimeProbability);
          
          return (
            <button
              key={project.projectCode}
              onClick={() => onProjectClick(project.projectCode)}
              className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-3 transition-all border border-gray-200 hover:border-[#DAA520] group"
            >
              {/* Project Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 group-hover:text-[#DAA520] transition-colors">
                      {project.projectName}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      {project.projectCode}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#DAA520] transition-colors ml-auto" />
                  </div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="grid grid-cols-3 gap-3 mb-2">
                {/* Completion */}
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    Completion
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-[#486581] to-[#829ab1] h-1.5 rounded-full transition-all"
                        style={{ width: `${project.completion}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 min-w-[35px]">
                      {project.completion}%
                    </span>
                  </div>
                </div>

                {/* On-Time Probability */}
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    On-Time Likelihood
                  </div>
                  <div className={`text-xs px-2 py-1 rounded border ${probabilityColors.bg} ${probabilityColors.text} ${probabilityColors.border} text-center font-medium`}>
                    {project.onTimeProbability}% Likely
                  </div>
                </div>

                {/* Risks */}
                <div>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Active Risks
                  </div>
                  <div className={`text-xs font-medium ${getRiskColor(project.risks)}`}>
                    {project.risks} {project.risks === 1 ? 'Risk' : 'Risks'}
                  </div>
                </div>
              </div>

              {/* Challenges */}
              {project.challenges.length > 0 && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">Key Challenges:</div>
                  <div className="flex flex-wrap gap-1">
                    {project.challenges.slice(0, 3).map((challenge, idx) => (
                      <span key={idx} className="text-xs bg-white border border-gray-300 text-gray-700 px-2 py-0.5 rounded">
                        {challenge}
                      </span>
                    ))}
                    {project.challenges.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{project.challenges.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-6 text-gray-500 text-sm">
          No projects in this program yet
        </div>
      )}
    </div>
  );
}

